import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, ArrowLeft, RotateCcw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { fetchMatchDaily, submitMatch } from '../../services/gamesApiWave2';
import GameResultCard from './GameResultCard';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Verse Match — memory pair-match. The server returns 6 verse/reference
 * pairs (12 cards total). Cards face down; player flips two; matches stay
 * revealed. Scoring is by total flip count: ≤16 = perfect (50pts),
 * ≤24 = 35pts, ≤36 = 20pts, otherwise 10pts.
 */
const FLIP_PEEK_MS = 900;

const shuffle = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const buildDeck = (pairs) => {
  // Each pair becomes two cards: one with the verse text, one with the reference.
  const cards = [];
  pairs.forEach((p) => {
    cards.push({ uid: `${p.pair_id}-text`, pairId: p.pair_id, kind: 'text', label: p.text });
    cards.push({ uid: `${p.pair_id}-ref`,  pairId: p.pair_id, kind: 'ref',  label: p.reference });
  });
  return shuffle(cards);
};

const VerseMatchGame = ({ language = 'en', onBack, onScored }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deck, setDeck] = useState([]);
  const [revealed, setRevealed] = useState(new Set()); // matched + currently flipped
  const [flipped, setFlipped] = useState([]);          // currently face-up unmatched (max 2)
  const [matched, setMatched] = useState(new Set());   // pair ids already matched
  const [flipCount, setFlipCount] = useState(0);
  const [startedAt, setStartedAt] = useState(null);
  const [result, setResult] = useState(null);
  const lockRef = useRef(false);

  useEffect(() => {
    setLoading(true);
    fetchMatchDaily()
      .then((d) => {
        setData(d);
        setDeck(buildDeck(d.pairs));
        setStartedAt(Date.now());
      })
      .catch(() => toast.error(t('games.failedLoad')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const totalPairs = data?.pairs?.length || 6;
  const allMatched = matched.size === totalPairs && totalPairs > 0;

  // Auto-submit once all pairs are matched.
  useEffect(() => {
    if (!allMatched || result) return;
    const time_ms = Date.now() - (startedAt || Date.now());
    submitMatch({ flips: flipCount, time_ms, completed: true })
      .then((r) => {
        setResult(r);
        onScored?.(r);
      })
      .catch(() => toast.error(t('games.match.submitFailed')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMatched, result, flipCount, startedAt, onScored, language]);

  const onCardClick = (card) => {
    if (lockRef.current) return;
    if (matched.has(card.pairId)) return;
    if (flipped.find((c) => c.uid === card.uid)) return;
    if (flipped.length >= 2) return;

    const newFlipped = [...flipped, card];
    setFlipped(newFlipped);
    setRevealed((s) => new Set(s).add(card.uid));
    setFlipCount((n) => n + 1);

    if (newFlipped.length === 2) {
      lockRef.current = true;
      const [a, b] = newFlipped;
      const isMatch = a.pairId === b.pairId && a.kind !== b.kind;
      if (isMatch) {
        setTimeout(() => {
          setMatched((m) => new Set(m).add(a.pairId));
          setFlipped([]);
          lockRef.current = false;
        }, 350);
      } else {
        setTimeout(() => {
          setRevealed((s) => {
            const ns = new Set(s);
            ns.delete(a.uid);
            ns.delete(b.uid);
            return ns;
          });
          setFlipped([]);
          lockRef.current = false;
        }, FLIP_PEEK_MS);
      }
    }
  };

  const restart = () => {
    if (!data) return;
    setDeck(buildDeck(data.pairs));
    setRevealed(new Set());
    setFlipped([]);
    setMatched(new Set());
    setFlipCount(0);
    setStartedAt(Date.now());
    setResult(null);
  };

  // Live elapsed time (no submit until all matched).
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startedAt || result) return;
    const timer = setInterval(() => setElapsed(Date.now() - startedAt), 1000);
    return () => clearInterval(timer);
  }, [startedAt, result]);

  const elapsedLabel = useMemo(() => {
    const s = Math.floor(elapsed / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }, [elapsed]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('games.loading')}
      </div>
    );
  }

  if (result) {
    return (
      <div className="space-y-4" data-testid="match-result-screen">
        <Button onClick={onBack} variant="ghost" size="sm" className="-ml-2">
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t('games.back')}
        </Button>
        <GameResultCard
          language={language}
          gameTitle={t('games.match.title')}
          correct={6}
          total={6}
          score={result.score}
          perfect={result.perfect}
          streak={result.streak}
          newTotal={result.new_total}
          subtitle={t('games.match.flipsSub', { flips: result.flips, secs: Math.floor(result.time_ms / 1000) })}
        />
        <div className="space-y-2">
          {data.pairs.map((p) => (
            <Card key={p.pair_id} className="border" data-testid={`match-review-${p.pair_id}`}>
              <CardContent className="p-3 text-sm">
                <p className="font-semibold text-purple-700 dark:text-purple-300">{p.reference}</p>
                <p className="italic text-muted-foreground">{p.text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="match-game-screen">
      <Button onClick={onBack} variant="ghost" size="sm" className="-ml-2">
        <ArrowLeft className="w-4 h-4 mr-1" />
        {t('games.back')}
      </Button>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">
          {t('games.match.title')}
        </h2>
        <div className="flex items-center gap-2 text-xs">
          <Badge variant="outline" data-testid="match-flip-count">
            {t('games.match.flips')}: <span className="ml-1 font-mono">{flipCount}</span>
          </Badge>
          <Badge variant="outline" data-testid="match-timer">
            <span className="font-mono">{elapsedLabel}</span>
          </Badge>
          <Badge variant="secondary">
            <Sparkles className="w-3 h-3 mr-0.5" />
            {matched.size}/{totalPairs}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="match-board">
        {deck.map((card) => {
          const isOpen = revealed.has(card.uid) || matched.has(card.pairId);
          const isMatched = matched.has(card.pairId);
          return (
            <button
              key={card.uid}
              type="button"
              onClick={() => onCardClick(card)}
              disabled={isMatched}
              data-testid={`match-card-${card.uid}`}
              className={`aspect-[3/4] rounded-md border text-xs leading-tight p-2 transition-all flex items-center justify-center text-center select-none ${
                isOpen
                  ? isMatched
                    ? 'bg-green-500/15 border-green-500/60 text-foreground'
                    : 'bg-purple-500/10 border-purple-500/60 text-foreground'
                  : 'bg-gradient-to-br from-purple-600 to-pink-600 border-transparent text-white hover:scale-[0.98] active:scale-95'
              }`}
            >
              {isOpen ? (
                <span className={card.kind === 'ref' ? 'font-semibold' : 'italic'}>
                  {card.label}
                </span>
              ) : (
                <Sparkles className="w-5 h-5 opacity-80" />
              )}
            </button>
          );
        })}
      </div>

      <div className="flex justify-end">
        <Button onClick={restart} variant="outline" size="sm" data-testid="match-restart-btn">
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
          {t('games.restart')}
        </Button>
      </div>
    </div>
  );
};

export default VerseMatchGame;
